<script setup lang="ts">
import { CalendarModeOptions } from 'frontcore/presentation/calendar/domain/types'
import { computed } from 'vue'

interface Props {
  date: Date,
  isDisabled: boolean,
  isCurrentDate: boolean,
  isEndDate: boolean,
  isInRange: boolean,
  isSelected: boolean,
  isStartDate: boolean,
  isLastDayOfWeek: boolean,
  isHoveredWeek: boolean,
  mode: number,
}
const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'mouseenter'): void,
  (e: 'mouseleave'): void,
  (e: 'click', event: Event): void
}>()

const dataTest = computed((): string => `calendar-menu-date-${props.date.getMonth()}-${props.date.getDate()}`)
</script>

<template>
  <li
    :data-test="dataTest"
    :class="{
      'calendar__menu__date--disabled': isDisabled,
      'calendar__menu__date--end': isEndDate,
      'calendar__menu__date__week--hovered': isHoveredWeek,
      'calendar__menu__date__week--end': isLastDayOfWeek,
      [mode === CalendarModeOptions.SELECT_WEEK ? 'calendar__menu__date__week--in-range' :'calendar__menu__date--in-range']: isInRange,
      [mode === CalendarModeOptions.SELECT_WEEK ? 'calendar__menu__date__week--selected' : 'calendar__menu__date--selected']: isSelected,
      'calendar__menu__date--start': isStartDate,
      'calendar__menu__date--today': isCurrentDate,
    }"
    class="calendar__menu__date flex-auto buttons--button-01"
    @click="emit('click', $event)"
    @mouseenter="emit('mouseenter')"
    @mouseleave="emit('mouseleave')">
    <div class="calendar__menu__date__range-indicator" />
    <div class="calendar__menu__date__item-container flex items-stretch justify-stretch flex-auto">
      <div class="calendar__menu__date__item flex items-center justify-center">
        <span class="calendar__menu__date-label">
          {{ date.getDate() }}
        </span>
      </div>
    </div>
  </li>
</template>

<style scoped>
.calendar__menu__date {
  position: relative;
  background-color: var(--core-neutral-cold-100);
  border-radius: var(--s-1);
}

.calendar__menu__date__item {
  width: var(--cell-size);
}

.calendar__menu__date__item-container, .calendar__menu__date__range-indicator {
  position: relative;
}

.calendar__menu__date__item-container:active {
  background-color: var(--core-primary-80);
}

.calendar__menu__date--selected .calendar__menu__date__item {
  background-color: var(--core-primary-80);
  border-radius: 100%;
}

.calendar__menu__date__item-container {
  border-radius: var(--s-1);
}

.calendar__menu__date__item-container:hover {
  background-color: var(--core-primary-90);
}

.calendar__menu__date--in-range .calendar__menu__date__range-indicator:before {
  content: '';
  position: absolute;
  top: 0;
  left: calc(-1 * var(--cell-gap));
  height: var(--cell-size);
  width: calc(var(--cell-size) + calc(2 * var(--cell-gap)));
  background-color: var(--core-neutral-cold-95);
}

.calendar__menu__date__week--hovered .calendar__menu__date__range-indicator:after,
.calendar__menu__date__week--hovered .calendar__menu__date__range-indicator:before,
.calendar__menu__date__week--in-range .calendar__menu__date__range-indicator:before {
  content: '';
  position: absolute;
  top: 0;
  left: calc(-1 * var(--cell-gap));
  height: var(--cell-size);
  width: calc(var(--cell-size) + calc(2 * var(--cell-gap)));
  background-color: var(--core-primary-90);
  z-index: 2;
}

.calendar__menu__date__week--in-range .calendar__menu__date__range-indicator:before {
  top: 0.14rem;
  height: calc(-0.28rem + var(--cell-size));
  background-color: var(--core-primary-80);
  z-index: 3;
}

.calendar__menu__date__week--hovered.calendar__menu__date__week--end .calendar__menu__date__range-indicator:before,
.calendar__menu__date__week--in-range.calendar__menu__date--end .calendar__menu__date__range-indicator:before,
.calendar__menu__date__week--hovered.calendar__menu__date__week--end .calendar__menu__date__range-indicator:after {
  border-radius: 0px 4px 4px 0px;
}

.calendar__menu__date__week--in-range.calendar__menu__date--end .calendar__menu__date__range-indicator:before {
  left: -0.25rem;
}

.calendar__menu__date-label {
  z-index: 4;
}

.calendar__menu__date--in-range.calendar__menu__date--start .calendar__menu__date__range-indicator:before {
  left: calc(var(--cell-size) / 2);
}

.calendar__menu__date--in-range.calendar__menu__date--start .calendar__menu__date__range-indicator:before,
.calendar__menu__date--in-range.calendar__menu__date--end .calendar__menu__date__range-indicator:before {
  width: calc(calc(var(--cell-size) / 2) + var(--cell-gap));
}

.calendar__menu__date--in-range.calendar__menu__date--start.calendar__menu__date--in-range.calendar__menu__date--end .calendar__menu__date__range-indicator:before {
  content: none;
}

.calendar__menu__date--today .calendar__menu__date__item-container:before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--cell-gap));
  left: calc(-1 * var(--cell-gap));
  height: calc(var(--cell-size) + calc(2 * var(--cell-gap)));
  width: calc(var(--cell-size) + calc(2 * var(--cell-gap)));
  border: 2px solid var(--core-primary-50);
  border-radius: 100%;
  z-index: 5;
}
.calendar__menu__date--disabled{
  color: var(--core-neutral-cold-60);
}
</style>
